import { useCallback, useRef, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  Typography,
} from '@mui/material'
import { QRCodeSVG } from 'qrcode.react'

import {
  ActionLink,
  ActionRow,
} from '@/apps/latest/components/molecules/ActionLink'
import { useSidebarYakurekiList } from '@/apps/latest/components/organisms/Sidebar/useSidebarYakurekiList'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { DialogHeader } from '@/components/atoms/DialogHeader'
import {
  boothQrValue,
  isBeforeGuidance,
  isInProgress,
  REMOTE_STATE_LABEL,
  type RemoteCall,
  useRemoteGuidanceMutation,
} from '@/features/remoteGuidance'
import { VideoCallRoom } from '@/features/videoCall'
import {
  generateTimestampLabel,
  useMyPharmacyQuery,
  usePatchYakurekiEventMutation,
} from '@/features/yakureki'
import { NOTION_DIALOG_PAPER_SX } from '@/shared/styles/dialogStyles'
import type { YakurekiDetail } from '@/shared/types/yakurekiCore'

// 遠隔服薬指導の依頼と状態の共有 (back log/250)。患者画面の下の行に 1 行だけ出す。
// 頼む店: 遠隔へ (録音の前だけ) → 札・取り消し・番号札 (仮) の印刷 → 指導済なら申し送り
// 遠隔の店: 札・受領・断る・指導を始める (依頼の時点から押せる) → 通話 → 指導を終える (申し送り)
//   指導を終えるは、通話が切れたときの窓と、指導中で通話が開いていないときの行の 2 か所から。
//   行に無いと「あとで」の後に終える手が無く、入り直して切ると 2 回目の音声で差し替わる
// 出し分けは依頼先の設定だけ (me/pharmacy の remote_guidance)。依頼先の無い店には何も出ない。
// 札の出し分けは back の is_live と state だけで決める。文言と「押せる札」の線は
// features/remoteGuidance に 1 つ (isInProgress / isBeforeGuidance)。
// この行は録音前 (Pre) と録音後 (Post) の画面の外に置かれ、SOAP ができて画面が切り替わっても
// 作り直されない (通話・申し送りの窓・送れなかった録音を失わないため。pages/index.tsx)

const minutesSince = (iso: string | null) =>
  iso
    ? `${Math.max(0, Math.floor((Date.now() - Date.parse(iso)) / 60000))}分`
    : ''

type NoteMode = 'request' | 'decline' | 'finish'

const NOTE_DIALOG: Record<
  NoteMode,
  { title: string; placeholder: string; confirm: string }
> = {
  request: {
    title: '遠隔へ依頼する',
    placeholder: '一言 (任意)　例: 後発品希望',
    confirm: '依頼する',
  },
  decline: {
    title: '依頼を断る',
    placeholder: '一言 (任意)　例: 本日は手が回りません',
    confirm: '断る',
  },
  finish: {
    title: '指導を終える',
    placeholder: '申し送り (任意)　例: 加算の確認をお願いします',
    confirm: '指導を終える',
  },
}

type UploadState = 'idle' | 'sending' | 'failed'

interface Props {
  event: YakurekiDetail
}

export const RemoteGuidanceRow = ({ event }: Props) => {
  const { data: pharmacy } = useMyPharmacyQuery()
  // 札は一覧 (2 秒ごとの差分) の方が新しい。一覧に無ければ詳細の値
  const { data: list } = useSidebarYakurekiList()
  const remote =
    list?.find((row) => row.id === event.id)?.remote ?? event.remote ?? null
  const mutation = useRemoteGuidanceMutation(event.id)
  const patchEvent = usePatchYakurekiEventMutation()

  const [noteMode, setNoteMode] = useState<NoteMode | null>(null)
  const [note, setNote] = useState('')
  const [call, setCall] = useState<RemoteCall | null>(null)
  const [upload, setUpload] = useState<UploadState>('idle')
  // 通話の録音。処方に入るまで持っておく (送れなかったら送り直せる)
  const pendingAudio = useRef<Blob | null>(null)
  const ticketRef = useRef<HTMLDivElement>(null)

  const toPharmacyName = pharmacy?.remote_guidance?.to_pharmacy_name
  const isIncoming = remote?.direction === 'incoming'
  // 生きていて、まだ終えていない (取り消し・お断り・指導済のどれでもない)
  const isOngoing = !!remote && isInProgress(remote)

  const openNote = (mode: NoteMode) => {
    setNote('')
    setNoteMode(mode)
  }

  // 通話が終わったらすぐ処方に送る (今ある録音の再アップロードと同じ経路。SOAP はここから作られる)。
  // 入り直して 2 回目の通話が終わると、その音声で差し替わる (前の通話の分は残らない。β の割り切り)
  const uploadAudio = useCallback(async () => {
    const blob = pendingAudio.current
    if (!blob) return
    setUpload('sending')
    try {
      await patchEvent.mutateAsync({
        audioSource: new File([blob], 'video-call.webm', {
          type: 'audio/webm',
        }),
        label: generateTimestampLabel(),
        yakurekiId: event.id,
      })
      pendingAudio.current = null
      setUpload('idle')
    } catch {
      // 文言は usePatchYakurekiEventMutation の onError が出す。送り直せるよう録音は持っておく
      setUpload('failed')
    }
  }, [patchEvent, event.id])

  const confirmNote = async () => {
    if (!noteMode) return
    try {
      if (noteMode === 'request') {
        await mutation.mutateAsync({ action: 'request', note })
      } else if (remote) {
        await mutation.mutateAsync({
          action: noteMode,
          remoteRequestId: remote.id,
          note,
        })
      }
    } catch {
      // 文言は useRemoteGuidanceMutation の onError が出す。窓は開けたままにする
      return
    }
    setNoteMode(null)
  }

  const startGuidance = () => {
    if (!remote) return
    mutation.mutate(
      { action: 'start', remoteRequestId: remote.id },
      {
        onSuccess: (res) => {
          if (res.call) setCall(res.call)
        },
      }
    )
  }

  const handleDisconnected = (blob: Blob | null) => {
    setCall(null)
    // 患者が着席して指導中になった通話だけ処方に送る。入室待ちのまま切った (薬剤師だけの)
    // 録音は捨てる。指導中は頼む店が取り消せない (back) ので、送った後に処方が消えることはない。
    // 札は一覧の 2 秒ごとの差分から読む (前面のタブでだけ取り直す)。着席の直後に切るか、通話中に
    // このタブを隠していたと、まだ入室待ちと読んで録音を捨てる。通話中はこのタブが前にあるので
    // β では直さない (back log/250 work-log/13 の既知の制約)
    if (blob && remote?.state === 'in_guidance') {
      pendingAudio.current = blob
      void uploadAudio()
    }
    // 切れたら申し送りの窓。「あとで」なら「指導を始める」で同じ通話に入り直せる
    openNote('finish')
  }

  const printTicket = () => {
    const win = window.open('', '_blank', 'width=360,height=480')
    if (!win) return
    const doc = win.document
    doc.title = '番号札'
    doc.body.style.cssText =
      'font-family:sans-serif;text-align:center;padding:24px'
    // 受付番号はレセコン由来の文字列。HTML として書かず textContent で入れる
    const line = (style: string) => {
      const el = doc.createElement('div')
      el.style.cssText = style
      doc.body.append(el)
      return el
    }
    line('font-size:14px').textContent = '遠隔服薬指導 番号札 (仮)'
    line('font-size:48px;font-weight:bold;margin:12px 0').textContent =
      event.queue_number ?? ''
    const svg = ticketRef.current?.firstElementChild
    if (svg) doc.body.append(doc.importNode(svg, true))
    line('font-size:12px;margin-top:12px').textContent =
      'ブースのカメラにかざしてください'
    win.focus()
    win.print()
  }

  // 頼む店で、録音の前 (status が pending) かつ、依頼が無い・取り消し・お断りの後: 「遠隔へ」。
  // 録音や SOAP のある処方は頼めない (遠隔の店が指導を終えると通話の音声で処方の音声と
  // SOAP が差し替わるため。back も同じ条件で拒む)
  const canRequest =
    !isIncoming &&
    !!toPharmacyName &&
    event.status === 'pending' &&
    (!remote || !remote.is_live)
  if (!remote && !canRequest) return null

  const tag = remote
    ? isIncoming
      ? `${remote.from_pharmacy_name}から：${REMOTE_STATE_LABEL[remote.state]}`
      : `遠隔（${remote.to_pharmacy_name}）：${
          REMOTE_STATE_LABEL[remote.state]
        }`
    : ''
  const quote =
    remote?.state === 'declined'
      ? remote.decline_note
      : remote?.state === 'finished'
      ? ''
      : remote?.request_note

  return (
    <>
      {/* 上の塊 (録音前の補助操作・録音後のカード) との間隔はこの行が持つ (置き場が Pre/Post の外のため) */}
      <ActionRow gapTop={2}>
        {remote && remote.state !== 'cancelled' && (
          <Typography
            component="span"
            sx={{ px: 1, fontSize: 13, color: colorTokens.fg.muted }}
          >
            {tag}
            {remote.state !== 'finished' &&
              ` ${minutesSince(remote.state_since)}`}
            {quote ? `「${quote}」` : ''}
          </Typography>
        )}
        {canRequest && (
          <ActionLink label="遠隔へ" beta onClick={() => openNote('request')} />
        )}
        {remote && !isIncoming && isOngoing && (
          <>
            {/* 指導中は取り消せない (back が 409。出口は遠隔の店の「指導を終える」だけ) */}
            {isBeforeGuidance(remote) && (
              <ActionLink
                label="取り消し"
                onClick={() =>
                  mutation.mutate({
                    action: 'cancel',
                    remoteRequestId: remote.id,
                  })
                }
              />
            )}
            <ActionLink
              label="番号札（仮）を印刷"
              disabled={!event.queue_number || !pharmacy}
              tooltip={event.queue_number ? undefined : '受付番号がありません'}
              onClick={printTicket}
            />
          </>
        )}
        {remote && isIncoming && isOngoing && (
          <>
            {remote.state === 'requested' && (
              <ActionLink
                label="受領"
                onClick={() =>
                  mutation.mutate({
                    action: 'accept',
                    remoteRequestId: remote.id,
                  })
                }
              />
            )}
            {isBeforeGuidance(remote) && (
              <ActionLink label="断る" onClick={() => openNote('decline')} />
            )}
            <ActionLink
              label="指導を始める"
              disabled={mutation.isPending}
              onClick={startGuidance}
            />
            {/* 指導中で通話が開いていないとき (「あとで」の後・窓を閉じた後) の、終える入口。
                通話中は切断の窓から終える。頼む店は指導中に取り消せないので、ここが唯一の出口 */}
            {remote.state === 'in_guidance' && !call && (
              <ActionLink
                label="指導を終える"
                onClick={() => openNote('finish')}
              />
            )}
          </>
        )}
        {upload === 'sending' && (
          <Typography
            component="span"
            sx={{ px: 1, fontSize: 13, color: colorTokens.fg.muted }}
          >
            通話の録音を送っています…
          </Typography>
        )}
        {upload === 'failed' && (
          <ActionLink
            label="通話の録音を送り直す"
            onClick={() => void uploadAudio()}
          />
        )}
      </ActionRow>
      {remote?.state === 'finished' && remote.handover_note && (
        <Typography
          sx={{ px: 1, pt: 0.5, fontSize: 13, whiteSpace: 'pre-wrap' }}
        >
          申し送り：{remote.handover_note}
        </Typography>
      )}

      {/* 番号札 (仮) の QR。印刷の窓に SVG を写すためだけに置く */}
      {pharmacy && event.queue_number && (
        <Box ref={ticketRef} sx={{ display: 'none' }}>
          <QRCodeSVG
            value={boothQrValue(pharmacy.id, event.queue_number)}
            size={200}
            level="M"
          />
        </Box>
      )}

      <Dialog
        open={noteMode !== null}
        onClose={() => setNoteMode(null)}
        fullWidth
        maxWidth="xs"
        sx={{ '& .MuiDialog-paper': NOTION_DIALOG_PAPER_SX }}
      >
        {noteMode && (
          <>
            <DialogHeader
              title={NOTE_DIALOG[noteMode].title}
              subtitle={
                noteMode === 'request' ? `依頼先: ${toPharmacyName}` : undefined
              }
              onClose={() => setNoteMode(null)}
            />
            <DialogContent sx={{ pt: 2 }}>
              <TextField
                autoFocus
                fullWidth
                multiline
                minRows={2}
                size="small"
                placeholder={NOTE_DIALOG[noteMode].placeholder}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                inputProps={{ maxLength: 500 }}
              />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => setNoteMode(null)} color="inherit">
                {noteMode === 'finish' ? 'あとで' : 'キャンセル'}
              </Button>
              <Button
                variant="contained"
                disableElevation
                disabled={mutation.isPending}
                onClick={confirmNote}
              >
                {NOTE_DIALOG[noteMode].confirm}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {call && (
        <VideoCallRoom
          livekitUrl={call.livekit_url}
          livekitToken={call.livekit_token}
          yakurekiId={event.id}
          currentPrescription={event.current_prescription_data ?? undefined}
          previousPrescription={event.previous_prescription_data ?? undefined}
          onDisconnected={handleDisconnected}
        />
      )}
    </>
  )
}
